import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { Check, Crown, Building2, Sparkles, Users } from 'lucide-react';
import { toast } from 'sonner';

const Membership = () => {
  const { user, token } = React.useContext(AuthContext);
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    fetchPackages();
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/membership/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const response = await axios.post(`${API}/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowCancelDialog(false);
      fetchSubscriptionStatus();
      // Reload user data
      window.location.reload();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await axios.post(`${API}/subscription/portal`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to open portal:', error);
      toast.error('Failed to open subscription management');
    }
  };

  const handleUpgrade = async (packageId) => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/checkout`,
        { package_id: packageId, origin_url: originUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const tiers = [
    {
      name: 'Free',
      icon: Sparkles,
      price: 'Free',
      description: 'Join the community',
      features: [
        'Basic profile creation',
        'View up to 5 members',
        'View up to 3 businesses',
        'Attend free community events',
        'View up to 5 opportunities per month',
        'Access to discussion forums'
      ],
      current: user?.membership_tier === 'free',
      buttonText: null,  // No button for free tier
      disabled: true,
      isFree: true  // Special flag for free tier
    },
    {
      name: 'Basic',
      icon: Users,
      price: '€25/mo',
      yearlyPrice: '€250/yr',
      packageMonthly: 'basic_monthly',
      packageYearly: 'basic_yearly',
      description: 'Small to mid-sized businesses',
      features: [
        'Full member profile and listing',
        'Access to networking features',
        'Attend free community events',
        'Access to discussion forums and group chats',
        'Full directory access',
        'Connect with all members'
      ],
      current: user?.membership_tier === 'basic',
      buttonText: user?.membership_tier === 'basic' ? 'Current Plan' : 'Upgrade to Basic',
      savings: '2 months free annually'
    },
    {
      name: 'Premium',
      icon: Crown,
      price: '€50/mo',
      yearlyPrice: '€500/yr',
      packageMonthly: 'premium_monthly',
      packageYearly: 'premium_yearly',
      description: 'Established businesses',
      features: [
        'Everything in Basic, plus:',
        'Post ads and featured listings',
        'Host and promote events on the platform',
        'Highlighted business profile on homepage',
        'Access to exclusive masterclasses and webinars',
        'Priority customer support'
      ],
      current: user?.membership_tier === 'premium',
      buttonText: user?.membership_tier === 'premium' ? 'Current Plan' : 'Upgrade to Premium',
      popular: true,
      savings: 'Save up to 2 months'
    },
    {
      name: 'Corporate',
      icon: Building2,
      price: '€120/mo',
      yearlyPrice: '€1,200/yr',
      packageMonthly: 'corporate_monthly',
      packageYearly: 'corporate_yearly',
      description: 'Medium to large enterprises',
      features: [
        'Everything in Premium, plus:',
        'Dedicated account management',
        'Corporate branding and partnership opportunities',
        'Access to early event sponsorships',
        'Priority listing in all community directories',
        'Invitations to VIP networking events and panels',
        'Lead and mentor within the community'
      ],
      current: user?.membership_tier === 'corporate',
      buttonText: user?.membership_tier === 'corporate' ? 'Current Plan' : 'Upgrade to Corporate'
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="membership-page">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Choose Your Membership
          </h1>
          <p className="text-lg text-gray-700">Unlock your full potential with the right plan for you</p>
          
          {/* Subscription Status */}
          {subscriptionStatus?.has_subscription && (
            <div className="mt-6 max-w-2xl mx-auto">
              <Card className="glass">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h3 className="font-semibold text-lg mb-1">Active Subscription</h3>
                      <p className="text-sm text-gray-600">
                        {subscriptionStatus.tier.charAt(0).toUpperCase() + subscriptionStatus.tier.slice(1)} • 
                        €{subscriptionStatus.plan_amount}/{subscriptionStatus.plan_interval}
                      </p>
                      {subscriptionStatus.cancel_at_period_end ? (
                        <Badge variant="outline" className="mt-2 bg-yellow-100 text-yellow-800">
                          Cancels on {new Date(subscriptionStatus.current_period_end).toLocaleDateString()}
                        </Badge>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">
                          Renews on {new Date(subscriptionStatus.current_period_end).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleManageSubscription}
                      >
                        Manage
                      </Button>
                      {!subscriptionStatus.cancel_at_period_end && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowCancelDialog(true)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {user?.membership_tier !== 'basic' && user?.membership_expires && !subscriptionStatus?.has_subscription && (
            <div className="mt-4">
              <Badge variant="outline" className="text-sm" data-testid="membership-expires">
                Your {user.membership_tier} membership expires on {new Date(user.membership_expires).toLocaleDateString()}
              </Badge>
            </div>
          )}
        </div>

        {/* Cancel Subscription Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Subscription?</DialogTitle>
              <DialogDescription>
                Your subscription will remain active until the end of your current billing period. 
                After that, you'll be downgraded to the Free tier.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
                Keep Subscription
              </Button>
              <Button 
                onClick={handleCancelSubscription}
                className="bg-red-600 hover:bg-red-700"
              >
                Cancel Subscription
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {tiers.map((tier, index) => {
            const Icon = tier.icon;
            return (
              <Card 
                key={tier.name} 
                className={`glass relative ${
                  tier.popular ? 'ring-2 ring-rose-500 shadow-2xl scale-105' : ''
                } hover:shadow-xl transition-all`}
                data-testid={`tier-${tier.name.toLowerCase()}`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-rose-500 text-white px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="text-center">
                  <Icon className="w-12 h-12 mx-auto mb-4 text-rose-500" />
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {tier.name}
                  </CardTitle>
                  <CardDescription>{tier.description}</CardDescription>
                  <div className="mt-4">
                    <div className="text-4xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                      {tier.price}
                    </div>
                    {tier.yearlyPrice && (
                      <div className="text-sm text-gray-600 mt-1">
                        or {tier.yearlyPrice} (save 17%)
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {!tier.disabled && !tier.current && (
                    <div className="space-y-2">
                      {tier.packageMonthly && (
                        <Button 
                          onClick={() => handleUpgrade(tier.packageMonthly)}
                          disabled={loading}
                          className="w-full bg-rose-500 hover:bg-rose-600"
                          data-testid={`upgrade-${tier.name.toLowerCase()}-monthly`}
                        >
                          Monthly Plan
                        </Button>
                      )}
                      {tier.packageYearly && (
                        <Button 
                          onClick={() => handleUpgrade(tier.packageYearly)}
                          disabled={loading}
                          variant="outline"
                          className="w-full"
                          data-testid={`upgrade-${tier.name.toLowerCase()}-yearly`}
                        >
                          Yearly Plan (Save 17%)
                        </Button>
                      )}
                    </div>
                  )}
                  {tier.current && tier.isFree && (
                    <div className="w-full py-3 text-center">
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        ✓ Your Current Plan
                      </Badge>
                    </div>
                  )}
                  {tier.current && !tier.isFree && (
                    <Button disabled className="w-full bg-green-500 hover:bg-green-600 text-white cursor-not-allowed" data-testid={`current-plan-${tier.name.toLowerCase()}`}>
                      ✓ {tier.buttonText}
                    </Button>
                  )}
                  {tier.disabled && !tier.current && (
                    <Button disabled className="w-full" data-testid="basic-plan-button">
                      {tier.buttonText}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Membership;